import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormInputFile } from '@/components/forms/form-input-file';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/components/ui/hooks/use-toast';
import { createLearningPathsByCsv, getLearningPaths } from '@/lib/api-calls';
import { csvFormSchema, parseCsvAndUpload } from '@/lib/csv-utils';
import { pluralize } from '@/lib/string-utils';
import { CreateLearningPathCsvFormKeys, createLearningPathCsvRow } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { z } from 'zod';

interface CreateLearningPathsByCsvProps {
  trigger: ReactNode;
}

const EMPTY_ERRORS: string[] = [];
const formSchema = csvFormSchema;

export const CreateLearningPathsByCsv = ({ trigger }: CreateLearningPathsByCsvProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [errorMessages, setErrorMessages] = useState<string[]>(EMPTY_ERRORS);
  const { mutateAsync } = useMutation({
    mutationFn: createLearningPathsByCsv,
  });

  const onSubmit = async ({ formData, closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    const { csvFile } = formData;

    const result = await parseCsvAndUpload({
      file: csvFile[0],
      rowSchema: createLearningPathCsvRow,
      onUpload: async (file) => {
        const uploadForm = new FormData();
        uploadForm.append(CreateLearningPathCsvFormKeys.csvFile, file);
        return mutateAsync(uploadForm);
      },
      onToast: toast,
      onSetErrors: setErrorMessages,
    });

    const { successRowsCount, hasErrors } = result;

    if (successRowsCount > 0) {
      toast({
        title: `${successRowsCount} ${pluralize(successRowsCount, 'row', 'rows')} imported for learning paths`,
      });
      queryClient.invalidateQueries(getLearningPaths());
    }

    if (!hasErrors) {
      closeModal();
    }

    return successRowsCount;
  };

  return (
    <FormModal
      modalTitle="Create learning paths by CSV"
      formSchema={formSchema}
      onSubmit={onSubmit}
      trigger={trigger}
      onClose={() => setErrorMessages(EMPTY_ERRORS)}
    >
      {({ form }) => {
        return (
          <>
            <div className="grid grid-cols-1 gap-2 py-4">
              <FormInputFile
                form={form}
                name="csvFile"
                label="Learning paths CSV"
                inputProps={{
                  accept: '.csv',
                  className: 'w-full',
                }}
              />
              <span>
                See{' '}
                <a
                  href="https://docs.google.com/spreadsheets/d/1rzbm2t8r1Wsa89DuYZEippWqLki20n_XV6Hp0KelsPw/edit?gid=1121158421#gid=1121158421"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-blue-600 hover:underline"
                >
                  template
                </a>{' '}
                for CSV template. Export the sheet as csv file and edit it. Do NOT change the header row
              </span>
            </div>
            {errorMessages.length > 0 && (
              <div className="w-full mb-4">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error while parsing CSV</AlertTitle>
                  <AlertDescription>
                    {errorMessages.map((message, index) => (
                      <p key={index}>{message}</p>
                    ))}
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </>
        );
      }}
    </FormModal>
  );
};
